/**
 * outlookComposeThunks.js
 *
 * Fresh Redux thunks for Outlook 2.0 compose operations.
 * Completely independent — does NOT import from notification.js.
 *
 * All API calls go through graphMailService → /api/mail/ (mailApiRoutes.js)
 * which proxies to Microsoft Graph using the app-only token.
 *
 * Attachment format expected by /api/mail/ routes:
 *   { name, contentType, contentBytes }   ← contentBytes is base64
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as graph from '../../services/graphMailService';
import { pushNotification } from '../mailUiSlice';
import { clearFolderThreads } from '../mailThreadsSlice';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Returns the Sent Items folder ID from Redux state, or null if not loaded. */
function getSentFolderId(state) {
  return Object.values(state.mailFolders.byId).find(f => f.wellKnownName === 'sentitems')?.id ?? null;
}

/** Read a File or Blob as base64 (no data-URL prefix). */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a raw File array (from <input type="file"> or state) into the
 * attachment shape that /api/mail/messages/* routes expect.
 *
 * Handles both native File objects and antd-upload file objects (originFileObj).
 */
async function filesToAttachments(fileList = []) {
  return Promise.all(
    fileList.map(async (f) => {
      const blob = f.originFileObj || f;
      return {
        name:         f.name,
        contentType:  f.type || 'application/octet-stream',
        contentBytes: await fileToBase64(blob),
      };
    })
  );
}

/**
 * Map a plain string email address to Graph recipient shape.
 * Accepts either a string or an already-shaped { emailAddress: { address } }.
 */
function toRecipient(addr) {
  if (typeof addr === 'string') {
    return { emailAddress: { address: addr } };
  }
  return addr; // already shaped
}

// ---------------------------------------------------------------------------
// SEND — new email
// ---------------------------------------------------------------------------

/**
 * Send a brand-new email.
 *
 * Payload:
 *   to[]               string[]  recipient addresses
 *   cc[]               string[]  (optional)
 *   bcc[]              string[]  (optional)
 *   subject            string
 *   bodyHtml           string    full HTML from contentEditable editor
 *   fileList           File[]    raw File objects (may be empty)
 *   importance         'normal' | 'high' | 'low'  (default: 'normal')
 *   requestReadReceipt boolean   (default: false)
 *
 * Backend: POST /api/mail/messages/send
 */
export const outlookSendThunk = createAsyncThunk(
  'outlook2/send',
  async (
    {
      to = [],
      cc = [],
      bcc = [],
      subject = '',
      bodyHtml = '',
      fileList = [],
      importance = 'normal',
      requestReadReceipt = false,
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const attachments = await filesToAttachments(fileList);

      await graph.sendMessage({
        to,
        cc,
        bcc,
        subject,
        bodyHtml,
        attachments,
        importance,
        requestReadReceipt,
      });

      dispatch(pushNotification({ type: 'success', message: 'Message sent', autoDismissMs: 3000 }));

      // Invalidate Sent Items so EmailList auto-refetches it
      const sentFolderId = getSentFolderId(getState());
      if (sentFolderId) dispatch(clearFolderThreads({ folderId: sentFolderId }));

      return { success: true };
    } catch (err) {
      const msg = err?.message || 'Failed to send message';
      dispatch(pushNotification({ type: 'error', message: msg, autoDismissMs: 5000 }));
      return rejectWithValue(msg);
    }
  }
);

// ---------------------------------------------------------------------------
// REPLY
// ---------------------------------------------------------------------------

/**
 * Reply to a single message.
 *
 * Payload:
 *   messageId    string    Graph message ID to reply to
 *   comment      string    full HTML body (user text + quoted block)
 *   toRecipients string[]  override To recipients (optional — Graph uses original sender by default)
 *   fileList     File[]    attachments (optional)
 *
 * Backend: POST /api/mail/messages/:messageId/reply
 */
export const outlookReplyThunk = createAsyncThunk(
  'outlook2/reply',
  async (
    { messageId, comment = '', toRecipients = [], fileList = [], requestReadReceipt = false },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const attachments = await filesToAttachments(fileList);

      const payload = { comment, requestReadReceipt };
      if (toRecipients.length) {
        payload.toRecipients = toRecipients.map(toRecipient);
      }
      if (attachments.length) {
        payload.attachments = attachments;
      }

      await graph.replyToMessage(messageId, payload);

      dispatch(pushNotification({ type: 'success', message: 'Reply sent', autoDismissMs: 3000 }));

      const sentFolder = Object.values(getState().mailFolders.byId)
        .find(f => f.wellKnownName === 'sentitems');
      if (sentFolder) dispatch(clearFolderThreads({ folderId: sentFolder.id }));

      return { success: true };
    } catch (err) {
      const msg = err?.message || 'Failed to send reply';
      dispatch(pushNotification({ type: 'error', message: msg, autoDismissMs: 5000 }));
      return rejectWithValue(msg);
    }
  }
);

/**
 * Reply-all to a message.
 *
 * Payload:
 *   messageId string   Graph message ID
 *   comment   string   full HTML body
 *   fileList  File[]   attachments (optional)
 *
 * Backend: POST /api/mail/messages/:messageId/replyAll
 */
export const outlookReplyAllThunk = createAsyncThunk(
  'outlook2/replyAll',
  async (
    { messageId, comment = '', fileList = [], requestReadReceipt = false },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const attachments = await filesToAttachments(fileList);

      const payload = { comment, requestReadReceipt };
      if (attachments.length) {
        payload.attachments = attachments;
      }

      await graph.replyAllToMessage(messageId, payload);

      dispatch(pushNotification({ type: 'success', message: 'Reply sent to all', autoDismissMs: 3000 }));

      const sentFolder = Object.values(getState().mailFolders.byId)
        .find(f => f.wellKnownName === 'sentitems');
      if (sentFolder) dispatch(clearFolderThreads({ folderId: sentFolder.id }));

      return { success: true };
    } catch (err) {
      const msg = err?.message || 'Failed to send reply all';
      dispatch(pushNotification({ type: 'error', message: msg, autoDismissMs: 5000 }));
      return rejectWithValue(msg);
    }
  }
);

// ---------------------------------------------------------------------------
// DRAFT LIFECYCLE
// ---------------------------------------------------------------------------

/**
 * Create or update a draft, then attach any files.
 *
 * Pass `draftId: null` to create (POST → returns new draftId).
 * Pass `draftId: string` to update (PATCH → same draftId returned).
 *
 * Payload:
 *   draftId    string | null
 *   to[]       string[]
 *   cc[]       string[]
 *   bcc[]      string[]
 *   subject    string
 *   bodyHtml   string
 *   fileList   File[]  (optional) — uploaded to the draft after save
 */
export const saveDraftThunk = createAsyncThunk(
  'outlook2/saveDraft',
  async (
    {
      draftId                = null,
      to                     = [],
      cc                     = [],
      bcc                    = [],
      subject                = '',
      bodyHtml               = '',
      fileList               = [],
      importance             = 'normal',
      requestReadReceipt     = false,
      scheduledSendDateTime  = undefined,
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const payload = {
        subject,
        bodyHtml,
        toRecipients:  to.map(a => ({ emailAddress: { address: a } })),
        ccRecipients:  cc.map(a => ({ emailAddress: { address: a } })),
        bccRecipients: bcc.map(a => ({ emailAddress: { address: a } })),
        importance,
        requestReadReceipt,
        ...(scheduledSendDateTime !== undefined ? { scheduledSendDateTime } : {}),
      };

      let activeDraftId;
      if (draftId) {
        await graph.updateDraft(draftId, payload);
        activeDraftId = draftId;
      } else {
        const result = await graph.createDraft(payload);
        activeDraftId = result?.id || result?.messageId || result;
      }

      // Upload each attachment to the draft individually
      if (fileList.length > 0) {
        const attachments = await filesToAttachments(fileList);
        await Promise.all(
          attachments.map(att => graph.addAttachmentToMessage(activeDraftId, att))
        );
      }

      return { draftId: activeDraftId };
    } catch (err) {
      const msg = err?.message || 'Failed to save draft';
      dispatch(pushNotification({ type: 'error', message: msg, autoDismissMs: 4000 }));
      return rejectWithValue(msg);
    }
  }
);

/**
 * Send a saved draft by its Graph message ID.
 *
 * Backend: POST /api/mail/messages/drafts/:draftId/send
 */
export const sendDraftThunk = createAsyncThunk(
  'outlook2/sendDraft',
  async ({ draftId }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Capture draft folder before sending — message is gone from Graph after this call
      const draftMsg      = getState().mailMessages?.byId?.[draftId];
      const draftFolderId = draftMsg?.folderId || null;

      await graph.sendDraft(draftId);
      dispatch(pushNotification({ type: 'success', message: 'Message sent', autoDismissMs: 3000 }));

      // Invalidate Sent Items + Drafts folder so both lists auto-refetch
      const sentFolderId = getSentFolderId(getState());
      if (sentFolderId)   dispatch(clearFolderThreads({ folderId: sentFolderId }));
      if (draftFolderId)  dispatch(clearFolderThreads({ folderId: draftFolderId }));

      return { success: true };
    } catch (err) {
      const msg = err?.message || 'Failed to send draft';
      dispatch(pushNotification({ type: 'error', message: msg, autoDismissMs: 5000 }));
      return rejectWithValue(msg);
    }
  }
);

/**
 * Delete (discard) a draft.
 *
 * Backend: DELETE /api/mail/messages/drafts/:draftId
 */
export const deleteDraftThunk = createAsyncThunk(
  'outlook2/deleteDraft',
  async ({ draftId }, { rejectWithValue }) => {
    try {
      await graph.deleteDraft(draftId);
      return { draftId };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to delete draft');
    }
  }
);

// ---------------------------------------------------------------------------
// FORWARD
// ---------------------------------------------------------------------------

/**
 * Forward a message.
 *
 * Payload:
 *   messageId    string    Graph message ID to forward
 *   comment      string    HTML body prepended above the forwarded content
 *   toRecipients string[]  recipient addresses (required)
 *   fileList     File[]    additional attachments (optional)
 *
 * Backend: POST /api/mail/messages/:messageId/forward
 */
export const outlookForwardThunk = createAsyncThunk(
  'outlook2/forward',
  async (
    { messageId, comment = '', toRecipients = [], fileList = [], requestReadReceipt = false },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const attachments = await filesToAttachments(fileList);

      const payload = {
        comment,
        toRecipients: toRecipients.map(toRecipient),
        requestReadReceipt,
      };
      if (attachments.length) {
        payload.attachments = attachments;
      }

      await graph.forwardMessage(messageId, payload);

      dispatch(pushNotification({ type: 'success', message: 'Message forwarded', autoDismissMs: 3000 }));

      const sentFolder = Object.values(getState().mailFolders.byId)
        .find(f => f.wellKnownName === 'sentitems');
      if (sentFolder) dispatch(clearFolderThreads({ folderId: sentFolder.id }));

      return { success: true };
    } catch (err) {
      const msg = err?.message || 'Failed to forward message';
      dispatch(pushNotification({ type: 'error', message: msg, autoDismissMs: 5000 }));
      return rejectWithValue(msg);
    }
  }
);

